/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Resync, reset CDC, or clear and fill", description = "Choose safely between the three run-level recovery actions — full resync, reset CDC, and clear and fill — and understand what each one does to your destination.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Resync, reset CDC, or clear and fill",
        description: "Choose safely between the three run-level recovery actions — full resync, reset CDC, and clear and fill — and understand what each one does to your destination.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Choose an action",
          "depth": 0,
          "id": "choose-an-action"
        }, {
          "value": "Full resync",
          "depth": 0,
          "id": "full-resync"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites"
        }, {
          "value": "Reset CDC",
          "depth": 0,
          "id": "reset-cdc"
        }, {
          "value": "Clear and fill",
          "depth": 0,
          "id": "clear-and-fill"
        }, {
          "value": "Verify the result",
          "depth": 0,
          "id": "verify-the-result"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      img: "img",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["Full resync, reset CDC, and clear and fill are one-off run controls that change how the next run treats your data — they are not ", _jsx(_components.a, {
            href: "/syncs/types-and-modes#sync-modes",
            children: "sync modes"
          }), "."]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams recovering or rebuilding a sync's state"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "A sync that has run at least once."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Each of these actions is high-risk in a different way, so choose based on what you need to happen on the next run. All three run from the sync's ", _jsx(_components.strong, {
          children: "..."
        }), " menu — click ", _jsx(_components.strong, {
          children: "Resync or reset..."
        }), " and pick an option."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-actions-menu.png",
          alt: "The sync actions menu with Resync or reset"
        })
      }), "\n", _jsx(_components.h2, {
        id: "choose-an-action",
        children: "Choose an action"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "What it does"
            }), _jsx(_components.th, {
              children: "When to use it"
            }), _jsx(_components.th, {
              children: "Primary risk"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#full-resync",
                children: "Full resync"
              })
            }), _jsxs(_components.td, {
              children: ["Reprocesses every row currently in the model as if it were newly ", _jsx(_components.strong, {
                children: "Added"
              }), ", ignoring prior state."]
            }), _jsx(_components.td, {
              children: "Rebuild destination state when reprocessing every row is safe."
            }), _jsx(_components.td, {
              children: "Duplicate records or repeated events for insert-mode syncs, event syncs, and webhook destinations."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#reset-cdc",
                children: "Reset CDC"
              })
            }), _jsx(_components.td, {
              children: "Clears change-data-capture state without sending the reset run's rows."
            }), _jsx(_components.td, {
              children: "Re-establish a fresh baseline when you don't want a backfill."
            }), _jsx(_components.td, {
              children: "The destination can drift, because the skipped changes are never sent."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#clear-and-fill",
                children: "Clear and fill"
              })
            }), _jsx(_components.td, {
              children: "Clears the destination list or segment, then re-fills it from the current model."
            }), _jsx(_components.td, {
              children: "Rebuild a replaceable audience or segment."
            }), _jsx(_components.td, {
              children: "A temporary empty state, and it clears the entire list — not only the records Hightouch added."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "full-resync",
        children: "Full resync"
      }), "\n", _jsxs(_components.p, {
        children: ["A full resync — labeled ", _jsx(_components.strong, {
          children: "Resync full query"
        }), " in the menu — resets the sync's tracking to its initial state, as if it were syncing for the first time. It processes every row currently in the model as an ", _jsx(_components.strong, {
          children: "Added"
        }), " row."]
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch tracks changes between runs and ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture-cdc",
          children: "sends only the necessary updates"
        }), ", so an initial run shows many ", _jsx(_components.strong, {
          children: "Added"
        }), " rows and later runs show mostly ", _jsx(_components.strong, {
          children: "Changed"
        }), " or ", _jsx(_components.strong, {
          children: "Removed"
        }), ". A full resync returns the sync to that initial all-", _jsx(_components.strong, {
          children: "Added"
        }), " state."]
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsx(_components.p, {
        children: "Check the following before you trigger a full resync:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Criteria"
            }), _jsx(_components.th, {
              children: "Explanation"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/types-and-modes#sync-modes",
                children: "Sync mode"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: [_jsxs(_components.a, {
                    href: "/syncs/types-and-modes#sync-modes",
                    children: [_jsx(_components.strong, {
                      children: "Update"
                    }), " or ", _jsx(_components.strong, {
                      children: "upsert"
                    })]
                  }), " syncs are safe to fully resync: an already-synced row updates the same record."]
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/syncs/types-and-modes#sync-modes",
                    children: _jsx(_components.strong, {
                      children: "Insert"
                    })
                  }), " syncs, ", _jsx(_components.a, {
                    href: "/syncs/types-and-modes#event-syncs",
                    children: "event syncs"
                  }), ", and ", _jsx(_components.a, {
                    href: "/destinations/http-request",
                    children: "webhook destinations"
                  }), " aren't recommended, because reprocessing rows can create duplicates."]
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/overview#delete-behavior",
                children: "Delete behavior"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["If your destination supports deleting records and you selected ", _jsx(_components.strong, {
                    children: "Do nothing"
                  }), " when records leave the query results, a full resync has no impact on deletes."]
                }), _jsxs("li", {
                  children: ["If you selected ", _jsx(_components.strong, {
                    children: "Clear fields"
                  }), " or ", _jsx(_components.strong, {
                    children: "Delete destination record"
                  }), ", that setting is ignored during a full resync. A full resync only processes rows that currently exist in the model, so there's no ", _jsx(_components.a, {
                    href: "/syncs/cdc",
                    children: "diffing"
                  }), " against the prior run to identify what was removed. ", _jsx(_components.strong, {
                    children: "Removed"
                  }), " rows are ignored even when delete behavior is enabled."]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "A full resync can create duplicates or other issues in your destination if the\nprerequisites above aren't met."
        })
      }), "\n", _jsx(_components.h2, {
        id: "reset-cdc",
        children: "Reset CDC"
      }), "\n", _jsxs(_components.p, {
        children: ["Reset CDC clears ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture"
        }), " state without resyncing records. Hightouch ignores the current state of your model and tracks only rows added, changed, or removed from now on. The next run captures a fresh snapshot of your model and sends nothing to the destination; runs after that send only new changes."]
      }), "\n", _jsx(_components.p, {
        children: "Reset CDC suits insert-only use cases like conversion events and operational alerts, where a backfill would create duplicate records."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Skipping the backfill can leave your destination out of sync with your model,\nbecause the changes in the reset run are never sent. Use Reset CDC only when\nthat drift is acceptable."
        })
      }), "\n", _jsx(_components.h2, {
        id: "clear-and-fill",
        children: "Clear and fill"
      }), "\n", _jsxs(_components.p, {
        children: ["When the destination supports clearing all records in a segment or audience, the ", _jsx(_components.strong, {
          children: "Resync or reset..."
        }), " modal offers a clear-and-fill option, ", _jsx(_components.strong, {
          children: "Clear list and trigger full resync"
        }), ". It works like a ", _jsx(_components.a, {
          href: "#full-resync",
          children: "full resync"
        }), " with one added step: before it reprocesses the model's rows, it clears every user or identifier from the segment or audience selected in the sync configuration."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "This clears the entire list, not only the records Hightouch added. It can undo\nchanges made by other tools managing that list, and the clear step can take\nseveral hours to complete."
        })
      }), "\n", _jsx(_components.h2, {
        id: "verify-the-result",
        children: "Verify the result"
      }), "\n", _jsxs(_components.p, {
        children: ["After the run finishes, open it from the ", _jsxs(_components.a, {
          href: "/syncs/debugger#view-run-details",
          children: [_jsx(_components.strong, {
            children: "Runs"
          }), " tab"]
        }), " and confirm it did what you expected:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Full resync"
          }), " — every row shows as ", _jsx(_components.strong, {
            children: "Added"
          }), ", and the operation count matches your model's row count."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Reset CDC"
          }), " — the run sent nothing to the destination (zero operations); the next scheduled run resumes normal diffing."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Clear and fill"
          }), " — the list emptied and then refilled to the current model's row count."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If the result isn't what you expected, use the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "debugger"
        }), " to inspect the run."]
      })]
    });
  }
}
export default MDXContent;
